import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, ArrowRight, ClipboardList, UserSearch, Coins, FolderOpen, History as HistoryIcon, FileText, FilePlus2, PackageCheck } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { C, STATUSES, toForm, toBody, inp, days, LEVEL, useClaimsConfig, refreshClaimSuggestions } from "@/lib/claims";
import { TypePill, StatusPill, SurveyorPill, PayPill } from "@/components/claims/ClaimBadges";
import { Section, CustomerVehicleFields, ClaimInfoFields, SurveyorFields, DatesFields, Sel, card } from "@/components/claims/ClaimForm";
import ClaimTimeline from "@/components/claims/ClaimTimeline";
import { ClaimNotes, ClaimDocuments } from "@/components/claims/ClaimNotesDocs";
import AccidentReportCard from "@/components/claims/AccidentReportCard";
import SurveyorTimebar from "@/components/claims/SurveyorTimebar";
import ClaimCostSettlement from "@/components/claims/ClaimCostSettlement";
import ClaimInvoicesTab from "@/components/claims/ClaimInvoicesTab";
import ClaimFinalisationTab from "@/components/claims/ClaimFinalisationTab";

const when = (iso) => new Date(iso).toLocaleString("en-SG", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const TABS = [["overview", "Overview", ClipboardList], ["surveyor", "Surveyor", UserSearch], ["cost", "Cost & Settlement", Coins], ["invoices", "Lump-Sum Invoice", FileText], ["finalisation", "Finalisation", PackageCheck], ["documents", "Documents", FolderOpen], ["history", "History", HistoryIcon]];

// Explicit stage change — the only way status moves; every change lands in history
function StageControl({ claim, onChanged }) {
  const idx = STATUSES.indexOf(claim.current_status);
  const [next, setNext] = useState(STATUSES[Math.min(idx + 1, STATUSES.length - 1)]);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNext(STATUSES[Math.min(idx + 1, STATUSES.length - 1)]); }, [idx]);
  const go = async () => {
    setBusy(true);
    try { await axios.post(`${C}/${claim.id}/status`, { status: next, remarks }); toast.success(`Stage → ${next}`); setRemarks(""); onChanged(); }
    catch (e) { toast.error("Could not change stage", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };
  const lvl = claim.aging.level;
  return (
    <div className={`${card} border-rose-500/25 p-3 grid gap-3 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-center`} data-testid="stage-control">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-rose-300 mb-1">Current stage <span className="font-mono-num text-slate-500">{idx + 1}/{STATUSES.length}</span></div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5"><StatusPill status={claim.current_status} aging={claim.aging} full testid="detail-status" />{lvl !== "ok" && lvl !== "done" && <span className={`text-[11px] font-semibold ${LEVEL[lvl]}`}>{lvl === "alert" ? "Stuck — follow up now" : "Follow up"}</span>}</div>
      </div>
      <Sel value={next} onChange={(e) => setNext(e.target.value)} testid="stage-select" className="h-9">{STATUSES.map((s, i) => <option key={s} value={s} disabled={s === claim.current_status}>{`${i + 1}. ${s}${s === claim.current_status ? " (current)" : ""}`}</option>)}</Sel>
      <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="stage-remarks" className={`${inp} h-9`} placeholder="Remarks (optional) — e.g. surveyor came 10am" />
      <Button onClick={go} disabled={busy || next === claim.current_status} data-testid="stage-apply" className="h-9 bg-rose-500 hover:bg-rose-400 text-slate-950 font-bold"><ArrowRight className="w-4 h-4 mr-1" /> Change stage</Button>
    </div>
  );
}

function History({ rows }) {
  return (
    <ol className="divide-y divide-white/[0.05]" data-testid="status-history">
      {[...rows].reverse().map((h) => (
        <li key={h.id} className="grid sm:grid-cols-[175px_minmax(0,1fr)] gap-x-3 gap-y-0.5 py-2 text-xs">
          <span className="font-mono-num text-slate-500 whitespace-nowrap">{when(h.changed_at)}</span>
          <span className="text-slate-300 min-w-0 break-anywhere"><span className="text-slate-500">{h.previous_status || "—"}</span> <span className="text-slate-600">→</span> <b className="text-slate-100">{h.new_status}</b>{h.remarks && <span className="text-slate-400"> · {h.remarks}</span>}<span className="text-slate-600"> · {h.changed_by}</span></span>
        </li>
      ))}
      {rows.length === 0 && <li className="py-3 text-xs text-slate-600">No stage changes yet</li>}
    </ol>
  );
}

const Fact = ({ l, v, testid }) => <div className="min-w-0"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{l}</div><div className="text-sm font-semibold text-slate-100 truncate" data-testid={testid}>{v}</div></div>;

function Header({ claim, onDelete, isOwner }) {
  const navigate = useNavigate();
  return (
    <div className={`${card} p-4 space-y-3`} data-testid="claim-header">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-mono-num text-xl sm:text-2xl font-black tracking-tight break-anywhere" data-testid="detail-claim-number">{claim.claim_number}</h1>
            <TypePill type={claim.claim_type} testid="detail-type" /><SurveyorPill type={claim.surveyor_type} /><PayPill computed={claim.computed} />
          </div>
          <p className="text-xs text-slate-500 mt-1">Created {when(claim.created_at)} by {claim.created_by} · at current stage {days(claim.aging.days_at_stage)}</p>
        </div>
        {isOwner && <Button variant="outline" size="sm" onClick={onDelete} data-testid="claim-delete" className="border-rose-500/30 bg-transparent text-rose-300 hover:bg-rose-500/10 h-8"><Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete</Button>}
        <Button size="sm" onClick={() => navigate(`/claims/${claim.id}/lumpsum/new`)} data-testid="header-draft-lumpsum" className="h-8 bg-rose-500 hover:bg-rose-400 text-slate-950 font-bold"><FilePlus2 className="w-3.5 h-3.5 mr-1.5" /> Draft Lump-Sum Invoice</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 pt-3 border-t border-white/[0.05]" data-testid="claim-facts">
        <Fact l="Registration" v={<span className="font-mono-num">{claim.plate || "—"}</span>} />
        <Fact l="Make / model" v={claim.vehicle || "—"} />
        <Fact l="Customer" v={claim.customer_id ? <Link to={`/customers/${claim.customer_id}`} className="text-amber-300 hover:underline" data-testid="detail-customer-link">{claim.customer_name || "CRM customer"}</Link> : claim.customer_name || "—"} />
        <Fact l="Phone" v={<span className="font-mono-num">{claim.customer_phone || "—"}</span>} />
        <Fact l="Insurer" v={claim.insurer || "—"} />
      </div>
    </div>
  );
}

function TabBar({ tab, setTab, counts }) {
  return (
    <div className="flex gap-1 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 border-b border-white/[0.06] [scrollbar-width:none]" role="tablist" data-testid="claim-tabs">
      {TABS.map(([k, l, Icon]) => (
        <button key={k} role="tab" type="button" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
          className={`relative shrink-0 inline-flex items-center gap-1.5 h-10 px-3 text-sm font-semibold whitespace-nowrap transition-colors ${tab === k ? "text-slate-50" : "text-slate-500 hover:text-slate-200"}`}>
          <Icon className={`w-4 h-4 ${tab === k ? "text-rose-300" : ""}`} />{l}
          {counts[k] > 0 && <span className="font-mono-num text-[10px] rounded-full bg-slate-800 text-slate-300 px-1.5 py-0.5 leading-none">{counts[k]}</span>}
          {tab === k && <span className="absolute left-2 right-2 -bottom-px h-0.5 rounded-full bg-rose-400" />}
        </button>
      ))}
    </div>
  );
}

export default function ClaimDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { isOwner } = useAuth();
  const cfg = useClaimsConfig();
  const [claim, setClaim] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const tab = TABS.some(([k]) => k === sp.get("tab")) ? sp.get("tab") : "overview";
  const setTab = (t) => { const n = new URLSearchParams(sp); t === "overview" ? n.delete("tab") : n.set("tab", t); setSp(n, { replace: true }); };
  const dirtyRef = useRef(false);
  // Refresh the read-only claim; pending edits in `form` survive unless the caller asks for a reset (after Save)
  const load = useCallback((reset = false) => axios.get(`${C}/${id}`).then(({ data }) => { setClaim(data); if (reset || !dirtyRef.current) { setForm(toForm(data)); setDirty(false); dirtyRef.current = false; } }).catch(() => toast.error("Claim not found")), [id]);
  useEffect(() => { load(true); }, [load]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const update = (f) => { setForm(f); setDirty(true); dirtyRef.current = true; };
  const save = async () => {
    setSaving(true);
    try { await axios.put(`${C}/${id}`, toBody(form)); refreshClaimSuggestions(); toast.success("Claim saved"); await load(true); }
    catch (e) { toast.error("Could not save", { description: typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Check the fields" }); }
    finally { setSaving(false); }
  };
  const del = async () => { if (!window.confirm(`Delete claim ${claim.claim_number}? Status history goes with it.`)) return; await axios.delete(`${C}/${id}`); toast.success("Claim deleted"); navigate("/claims"); };
  if (!claim || !form) return <div className="min-h-screen bg-[#0B0F17] text-slate-100"><AppHeader /><div className="p-10 text-center text-slate-500">Loading claim…</div></div>;
  const counts = { documents: (claim.documents || []).length + (claim.notes || []).length + (claim.accident_report ? 1 : 0), history: (claim.history || []).length };
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 pb-32 md:pb-8">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-3" data-testid="claim-detail-page">
        <div className="flex items-center justify-between gap-3">
          <Link to="/claims/list" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-rose-300" data-testid="claim-back"><ArrowLeft className="w-3.5 h-3.5" /> All claims</Link>
          <span className="text-[10px] uppercase tracking-[0.2em] text-rose-400 font-bold">Insurance claim</span>
        </div>
        <Header claim={claim} onDelete={del} isOwner={isOwner} />
        <StageControl claim={claim} onChanged={() => load()} />
        <TabBar tab={tab} setTab={setTab} counts={counts} />

        {tab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3" data-testid="tab-panel-overview">
            <div className="space-y-3 min-w-0">
              <Section title="A · Customer & vehicle" testid="sec-customer"><CustomerVehicleFields form={form} setForm={update} /></Section>
              <Section title="B · Claim information" testid="sec-info"><ClaimInfoFields form={form} setForm={update} insurers={cfg.insurers} /></Section>
            </div>
            <div className="space-y-3 min-w-0">
              <AccidentReportCard claim={claim} onChanged={() => load()} />
              <Section title="Claim progress" testid="sec-timeline"><ClaimTimeline claim={claim} /></Section>
            </div>
          </div>
        )}

        {tab === "surveyor" && (
          <div className="space-y-3" data-testid="tab-panel-surveyor">
            <Section title="C · Surveyor" testid="sec-surveyor"><SurveyorFields form={form} setForm={update} /></Section>
            <Section title="D · Surveyor progress" testid="sec-dates">
              <SurveyorTimebar form={form} setForm={(k, v) => update({ ...form, [k]: v })} />
              <div className="mt-4"><DatesFields form={form} setForm={update} /></div>
            </Section>
          </div>
        )}

        {tab === "invoices" && <ClaimInvoicesTab claim={claim} />}
        {tab === "finalisation" && <ClaimFinalisationTab claim={claim} />}

        {tab === "cost" && (
          <Section title="E · Claim cost & insurance settlement" testid="sec-cost" className="space-y-0">
            <ClaimCostSettlement form={form} setForm={update} />
          </Section>
        )}

        {tab === "documents" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="tab-panel-documents">
            <Section title="F · Notes" testid="sec-notes"><ClaimNotes claim={claim} onChanged={() => load()} /></Section>
            <Section title="G · Documents" testid="sec-documents"><ClaimDocuments claim={claim} onChanged={() => load()} /></Section>
          </div>
        )}

        {tab === "history" && (
          <Section title="H · Status history" testid="sec-history"><History rows={claim.history || []} /></Section>
        )}
      </main>
      {dirty && (
        <div className="fixed bottom-16 md:bottom-4 inset-x-0 z-40 px-4 pointer-events-none">
          <div className="max-w-6xl mx-auto flex justify-end">
            <div className="pointer-events-auto flex items-center gap-3 rounded-xl border border-rose-500/30 bg-[#0B0F17]/95 backdrop-blur px-3 py-2 shadow-2xl shadow-black/50" data-testid="unsaved-bar">
              <span className="text-xs text-slate-300 hidden sm:inline"><span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 mr-1.5 align-middle" />Unsaved changes</span>
              <Button onClick={save} disabled={saving} data-testid="claim-save" className="h-9 px-4 bg-rose-500 hover:bg-rose-400 text-slate-950 font-black"><Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
